import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Option, Select, Sheet,
    Stack, Tab, TabList, TabPanel, Table, Tabs, Typography, tabClasses,
} from '@mui/joy';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';

import { useAdmin } from '../../../AdminContext';
import {
    APIAdminAdsRuleLogs, APIAdminAdsRules, APIAdminAdsRunRecommendations,
    APIAdminAdsRunRules, APIAdminAdsRunStrategies, APIAdminAdsStrategies,
    APIAdminAdsStrategyLogs, adminAdsError,
} from '../../../api_admin_ads';
import {
    STRATEGY_LABELS, STRATEGY_PHASE_COLORS, STRATEGY_PHASE_LABELS, formatMoney,
} from '../../../components/Ads/adsConstants';


export default function AdminAdsOptimizationPage() {
    const { adminToken: token, has } = useAdmin();
    const canManage = has('ads.manage');

    const [strategies, setStrategies] = useState([]);
    const [counters, setCounters] = useState({});
    const [strategyLogs, setStrategyLogs] = useState([]);
    const [rules, setRules] = useState([]);
    const [ruleLogs, setRuleLogs] = useState([]);
    const [phase, setPhase] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(null);
    const [tab, setTab] = useState(0);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const [s, sl, r, rl] = await Promise.all([
                APIAdminAdsStrategies(token, { phase: phase || undefined, page_size: 50 }),
                APIAdminAdsStrategyLogs(token, { page_size: 50 }),
                APIAdminAdsRules(token, { page_size: 50 }),
                APIAdminAdsRuleLogs(token, { page_size: 50 }),
            ]);
            setStrategies(s?.items || []);
            setCounters(s?.counters || {});
            setStrategyLogs(sl?.items || []);
            setRules(r?.items || []);
            setRuleLogs(rl?.items || []);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить данные'));
        } finally {
            setLoading(false);
        }
    }, [token, phase]);

    useEffect(() => { load(); }, [load]);

    const run = async (key, fn, label) => {
        setBusy(key);
        setError(null);
        setNotice(null);
        try {
            const res = await fn();
            setNotice(`${label}: ${JSON.stringify(res?.stats || res?.result || {})}`);
            await load();
        } catch (e) {
            setError(adminAdsError(e));
        } finally {
            setBusy(null);
        }
    };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography level="h4">Автостратегии и правила</Typography>
                    <Typography level="body-sm" color="neutral">
                        Стратегии пересчитываются в :20 каждого часа, правила — в :40
                    </Typography>
                </Box>
                {canManage && (
                    <Stack direction="row" spacing={1}>
                        <Button size="sm" variant="outlined" color="neutral"
                                startDecorator={<PlayArrowRoundedIcon />}
                                loading={busy === 'strategies'}
                                onClick={() => run('strategies',
                                    () => APIAdminAdsRunStrategies(token), 'Стратегии')}>
                            Пересчитать ставки
                        </Button>
                        <Button size="sm" variant="outlined" color="neutral"
                                loading={busy === 'rules'}
                                onClick={() => run('rules',
                                    () => APIAdminAdsRunRules(token), 'Правила')}>
                            Прогнать правила
                        </Button>
                        <Button size="sm" variant="outlined" color="neutral"
                                loading={busy === 'reco'}
                                onClick={() => run('reco',
                                    () => APIAdminAdsRunRecommendations(token), 'Рекомендации')}>
                            Рекомендации
                        </Button>
                    </Stack>
                )}
            </Stack>

            {notice && (
                <Alert color="success" sx={{ mb: 2 }} endDecorator={
                    <Button size="sm" variant="plain" color="success" onClick={() => setNotice(null)}>
                        Скрыть
                    </Button>
                }>{notice}</Alert>
            )}
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip variant="soft" color="warning">Обучение: {counters.learning || 0}</Chip>
                <Chip variant="soft" color="success">Работают: {counters.running || 0}</Chip>
                <Chip variant="soft" color="primary">В ограничении: {counters.capped || 0}</Chip>
            </Stack>

            {Number(counters.capped) > 0 && (
                <Alert color="warning" sx={{ mb: 2 }}>
                    <Box>
                        <Typography level="title-sm">
                            {counters.capped} стратегий упёрлись в ограничение
                        </Typography>
                        <Typography level="body-sm">
                            Они не могут поднять ставку выше потолка или опустить ниже минимума
                            площадки. Обычно это значит, что рекламодатель задал слишком жёсткое
                            ограничение средней цены клика для своей конкуренции.
                        </Typography>
                    </Box>
                </Alert>
            )}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'transparent' }}>
                <TabList sx={{
                    mb: 2, borderRadius: 'md',
                    [`& .${tabClasses.root}.${tabClasses.selected}`]: {
                        bgcolor: 'primary.softBg', color: 'primary.700',
                    },
                }}>
                    <Tab>Стратегии ({strategies.length})</Tab>
                    <Tab>Изменения ставок ({strategyLogs.length})</Tab>
                    <Tab>Правила ({rules.length})</Tab>
                    <Tab>Срабатывания ({ruleLogs.length})</Tab>
                </TabList>

                <TabPanel value={0} sx={{ p: 0 }}>
                    <Stack direction="row" sx={{ mb: 1.5 }}>
                        <Select size="sm" value={phase} onChange={(_, v) => setPhase(v ?? '')}
                                sx={{ minWidth: 200 }}>
                            <Option value="">Все фазы</Option>
                            {Object.entries(STRATEGY_PHASE_LABELS).map(([value, label]) => (
                                <Option key={value} value={value}>{label}</Option>
                            ))}
                        </Select>
                    </Stack>

                    {loading ? (
                        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                    ) : (
                        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                            <Table sx={{ '--TableCell-paddingY': '10px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: 230 }}>Кампания</th>
                                        <th style={{ width: 190 }}>Рекламодатель</th>
                                        <th style={{ width: 210 }}>Стратегия</th>
                                        <th style={{ width: 150 }}>Фаза</th>
                                        <th style={{ width: 190 }}>Ставка и коридор</th>
                                        <th>Последнее решение</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {strategies.map((row) => (
                                        <tr key={row.campaign_name + row.account_id}>
                                            <td>{row.campaign_name}</td>
                                            <td>
                                                <Typography level="body-xs">
                                                    {row.account_title}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">
                                                    {STRATEGY_LABELS[row.bid_strategy] || row.bid_strategy}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Chip size="sm" variant="soft"
                                                      color={STRATEGY_PHASE_COLORS[row.phase] || 'neutral'}>
                                                    {row.phase_display
                                                        || STRATEGY_PHASE_LABELS[row.phase]
                                                        || row.phase}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                                    {formatMoney(row.current_bid)} ₽
                                                </Typography>
                                                <Typography level="body-xs" color="neutral">
                                                    {formatMoney(row.min_bid)} — {formatMoney(row.max_bid)} ₽
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs" color="neutral">
                                                    {row.last_reason || '—'}
                                                </Typography>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}
                </TabPanel>

                <TabPanel value={1} sx={{ p: 0 }}>
                    <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                        <Table sx={{ '--TableCell-paddingY': '10px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 170 }}>Когда</th>
                                    <th style={{ width: 230 }}>Кампания</th>
                                    <th style={{ width: 170 }}>Было → стало</th>
                                    <th>Причина</th>
                                </tr>
                            </thead>
                            <tbody>
                                {strategyLogs.map((log) => (
                                    <tr key={log.id}>
                                        <td>
                                            <Typography level="body-xs">
                                                {log.date_create
                                                    ? format(new Date(log.date_create),
                                                        'd MMM, HH:mm', { locale: ru })
                                                    : '—'}
                                            </Typography>
                                        </td>
                                        <td>{log.campaign_name}</td>
                                        <td>
                                            {formatMoney(log.old_bid)} → {formatMoney(log.new_bid)} ₽
                                        </td>
                                        <td>
                                            <Typography level="body-xs" color="neutral">
                                                {log.reason}
                                            </Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                </TabPanel>

                <TabPanel value={2} sx={{ p: 0 }}>
                    <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                        <Table sx={{ '--TableCell-paddingY': '10px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 220 }}>Правило</th>
                                    <th style={{ width: 230 }}>Кампания</th>
                                    <th style={{ width: 230 }}>Условие</th>
                                    <th style={{ width: 180 }}>Действие</th>
                                    <th style={{ width: 110 }}>Активно</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rules.map((rule) => (
                                    <tr key={rule.id}>
                                        <td>{rule.name}</td>
                                        <td>{rule.campaign_name || 'все кампании'}</td>
                                        <td>
                                            {rule.metric_display} {rule.operator_display} {rule.threshold}
                                        </td>
                                        <td>{rule.action_display}</td>
                                        <td>
                                            <Chip size="sm" variant="soft"
                                                  color={rule.is_enabled ? 'success' : 'neutral'}>
                                                {rule.is_enabled ? 'да' : 'нет'}
                                            </Chip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                </TabPanel>

                <TabPanel value={3} sx={{ p: 0 }}>
                    <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                        <Table sx={{ '--TableCell-paddingY': '10px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 170 }}>Когда</th>
                                    <th style={{ width: 200 }}>Правило</th>
                                    <th style={{ width: 230 }}>Кампания</th>
                                    <th style={{ width: 130 }}>Значение</th>
                                    <th>Результат</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ruleLogs.map((log) => (
                                    <tr key={log.id}>
                                        <td>
                                            <Typography level="body-xs">
                                                {log.date_create
                                                    ? format(new Date(log.date_create),
                                                        'd MMM, HH:mm', { locale: ru })
                                                    : '—'}
                                            </Typography>
                                        </td>
                                        <td>{log.rule_name}</td>
                                        <td>{log.campaign_name}</td>
                                        <td>{Number(log.metric_value).toFixed(2)}</td>
                                        <td>
                                            <Typography level="body-xs" color="neutral">
                                                {log.action_display} · {log.result}
                                            </Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                </TabPanel>
            </Tabs>
        </Box>
    );
}